<template>
  <div class="otb-summary-details">
    <div class="otb-summary-details__dealer" v-if="showDealerDetails">
      <wb-icon
        class="otb-summary-details__icon"
        aria-hidden="true"
        name="bds/location-dealer/16"
      />
      <span class="otb-summary-details__dealer-text wb-type-copy-strong">{{
        dealerInformation.computedName
      }}</span>
      <button
        @click="toggleDealerInfoModal"
        class="otb-summary-details__dealer-info-button wb-button wb-button--tertiary wb-button--small"
      >
        {{ $t('summaryDetails.dealer.moreInfo') }}
      </button>
    </div>
    <div class="otb-summary-details__date" v-if="!requestMode">
      <div class="otb-summary-details__date-left">
        <wb-icon
          class="otb-summary-details__icon"
          aria-hidden="true"
          name="time"
        />
        <span class="wb-type-copy-strong">{{ dateInformation.day }}</span>
      </div>
      <div class="otb-summary-details__date-right">
        <span class="wb-type-copy-secondary">{{ dateInformation.time }}</span>
      </div>
    </div>

    <template v-if="dealerInfoModalOpen">
      <wb-modal
        mode="edit"
        close-on-backdrop-click
        close-on-escape-key
        scroll-lock
        @wbclose="toggleDealerInfoModal"
      >
        <wb-modal-header>
          <button
            @click="toggleDealerInfoModal"
            class="wb-accordion__close wb-button--close wb-button wb-button--tertiary wb-button-small"
          >
            <wb-icon
              class="wb-button__icon"
              aria-hidden="true"
              name="bds/chevron-left/24"
            >
            </wb-icon>
            {{ $t('schedule.filter.back') }}
          </button>
        </wb-modal-header>
        <wb-modal-content>
          <div class="schedule-modal-header">
            <widget-loader
              id="dealerWidget"
              ref="dealerWidget"
              @resources-loaded="dealerInitWidget"
              :resources="dealerSearchResources"
            />
          </div>
        </wb-modal-content>
      </wb-modal>
    </template>
    <vehicle-information
      :vehicleInformation="vehicleInformation"
      :theme="vehicleInformationTheme"
      :path="path"
      :paddings="'none'"
    ></vehicle-information>

    <div
      v-if="showWeightNotification"
      class="otb-summary-details__weight__notification"
    >
      <wb-icon name="bds/info-circle/24"></wb-icon>
      <p
        class="otb-summary-details__weight__notification__text wb-type-copy-secondary"
      >
        {{ $t('schedule.booking.vehicleTile.weight.tooltip') }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { VehicleInformation, WidgetLoader } from '../index';

// Models
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import {
  CONTEXT_OPTIONS,
  WEIGHT_CATEGORY,
} from '../../../../otbc/src/models/constants';
import useDealerLocator from '../../composables/dealer-locator.composable';
import { useStore } from '../../composables/store.composable';
import { Dealer } from '../../graphql/schema';
import { DateTime } from '../../models/entities/summary.model';
export default defineComponent({
  i18n,
  name: 'otb-summary-details',
  components: {
    'vehicle-information': VehicleInformation,
    'widget-loader': WidgetLoader,
  },
  props: {
    vehicleInformation: {
      type: VehicleInformation,
      default: {},
    },
    dealerInformation: {
      type: Object as () => Dealer,
      required: true,
    },
    path: {
      type: String,
      default: '',
    },
    dateInformation: {
      type: Object as () => DateTime,
      default: () => {
        return { day: '', time: '' };
      },
    },
    requestMode: Boolean,
  },
  setup(props, { emit }) {
    const { isMobile, isSmallScreen } = useMediaQuery();
    let dealerInfoModalOpen = ref(false);
    const dealerWidget = ref();
    const { state } = useStore();

    const { dealerSearchResources, initWidget } = useDealerLocator();

    const toggleDealerInfoModal = () => {
      dealerInfoModalOpen.value = !dealerInfoModalOpen.value;
    };

    const dealerInitWidget = async () => {
      initWidget(dealerWidget.value.$el, props.dealerInformation?.id);
    };

    const showDealerDetails = computed(() => {
      return (
        !props.requestMode ||
        (state.otbcFlow.fixedDealer && !state.otbcFlow.skippedResultsPage)
      );
    });

    const showWeightNotification = computed(() => {
      if (state.configuration.context === CONTEXT_OPTIONS.CARS) return false;
      return (
        state.otbcFlow.confirmedWeight === WEIGHT_CATEGORY.HEAVY_WEIGHT ||
        state.otbcFlow.confirmedMotorisation.weightCategory ===
          WEIGHT_CATEGORY.HEAVY_WEIGHT ||
        state.otbcFlow.selectedVehicle.weightCategory ===
          WEIGHT_CATEGORY.HEAVY_WEIGHT
      );
    });

    const vehicleInformationTheme = computed(() => {
      const isRequestInTablet = props.requestMode && isSmallScreen.value;
      return isMobile.value || isRequestInTablet ? 'white' : 'default';
    });

    return {
      emit,
      // methods
      toggleDealerInfoModal,
      dealerInitWidget,
      // computed
      dealerSearchResources,
      showDealerDetails,
      showWeightNotification,
      vehicleInformationTheme,
      // refs
      dealerInfoModalOpen,
      dealerWidget,
    };
  },
});
</script>

<style scoped lang="scss" src="./summary-details.scss"></style>
